import * as THREE from 'three';

/**
 * Central palette for vertex-colored lamella: same source as BSHStorageInfotainment pickBorderColors.
 * Use for createSegmentColorMaterial or for two-mesh fallback (palette[0], palette[1]).
 *
 * @param {Object} item - Lamella/layer item (may have combinedColors, joinedDim.combinedColors, orderColor, qualityColor, woodTypeColor).
 * @param {Object} options - { orderColor?: boolean, layerColor?: boolean, defaultMaterial?: any }
 * @returns {string[]|null} Array of hex color strings, or null if no palette.
 */
/** Take orderColor/combinedColors from first nested layer if item is a compartment (e.g. paternoster slot). */
function getOrderColorFromFirstLayer(item) {
  const first = (Array.isArray(item?.lamellas) && item.lamellas.length > 0 && item.lamellas[0]) || (Array.isArray(item?.layers) && item.layers.length > 0 && item.layers[0]) || null;
  if (!first) return { orderColorC: null, combinedColorsArray: [] };
  const raw =
    (Array.isArray(first?.combinedColors) && first.combinedColors.length ? first.combinedColors : []) || (Array.isArray(first?.joinedDim?.combinedColors) ? first.joinedDim.combinedColors : []) || [];
  return {
    orderColorC: first?.orderColor ?? null,
    combinedColorsArray: raw.filter((c) => c != null && c !== '')
  };
}

export function getVertexColorPalette(item, options = {}) {
  const { orderColor = false, layerColor = false, defaultMaterial = null } = options;
  let orderColorC = item?.orderColor ?? defaultMaterial;
  let combinedColorsFirst = item?.combinedColors?.[0] ?? defaultMaterial;
  const qualityColor = item?.qualityColor ?? defaultMaterial;
  const woodTypeColor = item?.woodTypeColor ?? defaultMaterial;

  const combinedColorsArray = (() => {
    const raw = (Array.isArray(item?.combinedColors) && item.combinedColors.length ? item.combinedColors : Array.isArray(item?.joinedDim?.combinedColors) ? item.joinedDim.combinedColors : []) || [];
    return raw.filter((c) => c != null && c !== '');
  })();

  if (orderColor) {
    if (combinedColorsArray.length >= 1) return combinedColorsArray;
    if ((orderColorC == null || orderColorC === defaultMaterial) && combinedColorsArray.length === 0) {
      const fromLayer = getOrderColorFromFirstLayer(item);
      if (fromLayer.combinedColorsArray.length >= 1) return fromLayer.combinedColorsArray;
      if (fromLayer.orderColorC != null && fromLayer.orderColorC !== '') {
        orderColorC = fromLayer.orderColorC;
        combinedColorsFirst = fromLayer.combinedColorsArray[0] ?? orderColorC;
      }
    }
    const c1 = combinedColorsFirst || orderColorC || '#888888';
    const c2 = orderColorC || combinedColorsFirst || '#888888';
    return [c1, c2];
  }
  if (layerColor && woodTypeColor != null && woodTypeColor !== '' && qualityColor != null && qualityColor !== '') {
    return [woodTypeColor, qualityColor];
  }
  if (layerColor && woodTypeColor != null && woodTypeColor !== '') {
    return [woodTypeColor, qualityColor || woodTypeColor];
  }
  if (layerColor && qualityColor != null && qualityColor !== '') {
    return [woodTypeColor || qualityColor, qualityColor];
  }
  return null;
}

/**
 * Shallow array compare for props comparison (avoid re-render when array content unchanged).
 */
function shallowArrEq(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Returns true when props are "equal" for render – skip re-render. Use with React.memo(Comp, arePropsEqual).
 * Compares item via getItemColorKey, geometry ref, scale/position/rotation arrays, and other primitives.
 */
export function layers3dPropsEqual(prev, next) {
  if (prev === next) return true;
  return (
    getItemColorKey(prev?.item) === getItemColorKey(next?.item) &&
    prev?.geometry === next?.geometry &&
    shallowArrEq(prev?.scale, next?.scale) &&
    prev?.orderColor === next?.orderColor &&
    prev?.layerColor === next?.layerColor &&
    prev?.material === next?.material &&
    prev?.texture === next?.texture &&
    prev?.item?.orderId === next?.item?.orderId &&
    prev?.item?.id === next?.item?.id &&
    prev?.item?.full === next?.item?.full &&
    prev?.item?.present === next?.item?.present &&
    prev?.item?.lamellaLength === next?.item?.lamellaLength &&
    (prev?.item?.piecesPerLayer ?? prev?.item?.count ?? prev?.item?.numberOfLamellas) === (next?.item?.piecesPerLayer ?? next?.item?.count ?? next?.item?.numberOfLamellas) &&
    shallowArrEq(prev?.startingVector, next?.startingVector) &&
    shallowArrEq(prev?.endVector, next?.endVector) &&
    shallowArrEq(prev?.rotation, next?.rotation) &&
    prev?.positionAnimation?.animationKey === next?.positionAnimation?.animationKey &&
    prev?.rotationAnimation?.animationKey === next?.rotationAnimation?.animationKey &&
    prev?.movingSpeed === next?.movingSpeed &&
    prev?.roundNumbers === next?.roundNumbers &&
    prev?.customText === next?.customText &&
    prev?.customTextFontSize === next?.customTextFontSize
  );
}

/**
 * Returns true when props are equal for LamellaStack3d – skip re-render.
 */
export function lamellaStack3dPropsEqual(prev, next) {
  if (prev === next) return true;
  return (
    getItemColorKey(prev?.item) === getItemColorKey(next?.item) &&
    prev?.geometry === next?.geometry &&
    shallowArrEq(prev?.scale, next?.scale) &&
    prev?.item?.actualLength === next?.item?.actualLength &&
    prev?.item?.actualThickness === next?.item?.actualThickness &&
    prev?.item?.actualWidth === next?.item?.actualWidth &&
    prev?.item?.taken === next?.item?.taken &&
    prev?.isBodenlager === next?.isBodenlager &&
    prev?.isFlexi === next?.isFlexi &&
    prev?.isExit === next?.isExit &&
    prev?.fullColor === next?.fullColor &&
    prev?.material === next?.material &&
    shallowArrEq(prev?.startingVector, next?.startingVector) &&
    shallowArrEq(prev?.endVector, next?.endVector) &&
    shallowArrEq(prev?.startingRotation, next?.startingRotation) &&
    shallowArrEq(prev?.rotation, next?.rotation) &&
    prev?.item?.id === next?.item?.id &&
    prev?.item?.orderId === next?.item?.orderId &&
    prev?.item?.full === next?.item?.full &&
    prev?.item?.numberOfLamellas === next?.item?.numberOfLamellas &&
    prev?.customTextContent === next?.customTextContent &&
    prev?.showText === next?.showText &&
    shallowArrEq(prev?.textPosition, next?.textPosition) &&
    prev?.customTextFontSize === next?.customTextFontSize &&
    prev?.invColor === next?.invColor &&
    prev?.white === next?.white
  );
}

/**
 * Returns true when props are equal for LamellaStackInstanced3d – skip re-render.
 */
export function lamellaStackInstanced3dPropsEqual(prev, next) {
  if (prev === next) return true;
  return (
    getItemColorKey(prev?.item) === getItemColorKey(next?.item) &&
    prev?.geometry === next?.geometry &&
    prev?.count === next?.count &&
    shallowArrEq(prev?.scale, next?.scale) &&
    prev?.stepY === next?.stepY &&
    shallowArrEq(prev?.basePosition, next?.basePosition) &&
    shallowArrEq(prev?.startingRotation, next?.startingRotation) &&
    prev?.item?.taken === next?.item?.taken &&
    prev?.fullColor === next?.fullColor &&
    prev?.material === next?.material &&
    prev?.item?.id === next?.item?.id &&
    prev?.item?.orderId === next?.item?.orderId &&
    prev?.item?.full === next?.item?.full &&
    prev?.item?.numberOfLamellas === next?.item?.numberOfLamellas &&
    prev?.customTextContent === next?.customTextContent &&
    prev?.showText === next?.showText &&
    shallowArrEq(prev?.textPosition, next?.textPosition) &&
    prev?.customTextFontSize === next?.customTextFontSize &&
    prev?.invColor === next?.invColor &&
    prev?.white === next?.white &&
    shallowArrEq(prev?.rotation, next?.rotation)
  );
}

/**
 * Stable string key from item's color-relevant fields. Use as useMemo dep so palette
 * is only recomputed when CLT status actually changes colors, not every 200ms tick.
 * @param {Object} item - Lamella/layer item from status
 * @returns {string}
 */
export function getItemColorKey(item) {
  if (!item) return '';
  const o = item.orderColor ?? '';
  const cc = Array.isArray(item.combinedColors) ? item.combinedColors.filter((c) => c != null && c !== '').join(',') : '';
  const jd = Array.isArray(item.joinedDim?.combinedColors) ? item.joinedDim.combinedColors.filter((c) => c != null && c !== '').join(',') : '';
  const first = (Array.isArray(item.lamellas) && item.lamellas[0]) || (Array.isArray(item.layers) && item.layers[0]);
  let fo = '';
  let fc = '';
  if (first) {
    fo = first.orderColor ?? '';
    fc = (Array.isArray(first.combinedColors) && first.combinedColors.length ? first.combinedColors : first.joinedDim?.combinedColors || []).filter((c) => c != null && c !== '').join(',');
  }
  const wt = item.woodTypeColor ?? '';
  const qc = item.qualityColor ?? '';
  return [o, cc, jd, fo, fc, wt, qc].join('|');
}

// ---------------------------------------------------------------------------
// GPU segment-color shader material
// All coloring is done per-pixel in the fragment shader -- zero CPU geometry work.
// ---------------------------------------------------------------------------

/**
 * Maximum segments supported by GPU uniform array.
 * WebGL guarantees at least 256 uniform vectors; 70 colors × 3 floats = 210 floats.
 * This leaves headroom for other uniforms in the shader.
 */
const SEG_MAX = 70;
const _c = new THREE.Color();

function computeDefaultBoundaries(numSegments, axisMin, axisMax, axis) {
  if (numSegments <= 1) return [];
  // Z-axis with 2 segments: 2/3 split (matching legacy behaviour)
  if (axis === 'z' && numSegments === 2) {
    return [axisMin + (2 / 3) * (axisMax - axisMin)];
  }
  const out = [];
  for (let s = 1; s < numSegments; s++) {
    out.push(axisMin + (s / numSegments) * (axisMax - axisMin));
  }
  return out;
}

/**
 * Creates a MeshStandardMaterial that colours segments along an axis entirely on the GPU.
 * No geometry cloning, no CPU triangle clipping, no computeVertexNormals -- just a patched
 * standard material that reads object-space position in the fragment shader.
 *
 * @param {string[]} colors - Hex color strings, one per segment (max 70).
 * @param {THREE.BufferGeometry} geometry - Source geometry (bounding box is read, geometry is NOT mutated).
 * @param {Object} [options]
 * @param {'z'|'x'} [options.axis='z'] - Segmentation axis.
 * @param {number[]} [options.boundaries] - Custom boundary positions (length = colors.length - 1).
 * @param {boolean} [options.reverseNegativeFaces] - Reverse segment order on negative-facing faces (default true for Z, false for X).
 * @param {boolean} [options.transparent=false]
 * @param {number}  [options.opacity=1.0]
 * @returns {THREE.MeshStandardMaterial}
 */
export function createSegmentColorMaterial(colors, geometry, options = {}) {
  const { axis = 'z', boundaries = null, reverseNegativeFaces = axis === 'z', transparent = false, opacity = 1.0 } = options;

  if (!colors?.length || !geometry) {
    return new THREE.MeshStandardMaterial({ transparent, opacity });
  }

  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) return new THREE.MeshStandardMaterial({ transparent, opacity });

  const numSegments = Math.min(Math.max(1, colors.length), SEG_MAX);
  if (process.env.NODE_ENV !== 'production' && colors.length > SEG_MAX) {
    console.warn(`createSegmentColorMaterial: ${colors.length} colors exceed SEG_MAX (${SEG_MAX}), truncating`);
  }

  // Use Float32Array for better memory management (avoids creating 70 Vector3 objects each call)
  const colorsArray = new Float32Array(SEG_MAX * 3);
  for (let i = 0; i < SEG_MAX; i++) {
    if (i < numSegments) {
      _c.set(colors[i] || '#888888');
    } else {
      _c.set('#888888');
    }
    colorsArray[i * 3] = _c.r;
    colorsArray[i * 3 + 1] = _c.g;
    colorsArray[i * 3 + 2] = _c.b;
  }

  const axisMin = axis === 'x' ? box.min.x : box.min.z;
  const axisMax = axis === 'x' ? box.max.x : box.max.z;

  let bounds;
  if (Array.isArray(boundaries) && boundaries.length === numSegments - 1) {
    bounds = boundaries;
  } else {
    bounds = computeDefaultBoundaries(numSegments, axisMin, axisMax, axis);
  }
  const boundsArr = new Float32Array(SEG_MAX);
  for (let i = 0; i < bounds.length && i < SEG_MAX; i++) {
    boundsArr[i] = bounds[i];
  }

  const material = new THREE.MeshStandardMaterial({ transparent, opacity });

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uSegColors = { value: colorsArray };
    shader.uniforms.uSegCount = { value: numSegments };
    shader.uniforms.uSegBounds = { value: boundsArr };
    shader.uniforms.uSegAxis = { value: axis === 'x' ? 0 : 2 };
    shader.uniforms.uSegReverse = { value: reverseNegativeFaces };

    shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nvarying vec3 vSegPos;\nvarying vec3 vSegNorm;');
    shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvSegPos = position;\nvSegNorm = normal;');

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      [
        '#include <common>',
        '#define SEG_MAX ' + SEG_MAX,
        'uniform vec3 uSegColors[SEG_MAX];',
        'uniform int uSegCount;',
        'uniform float uSegBounds[SEG_MAX];',
        'uniform int uSegAxis;',
        'uniform bool uSegReverse;',
        'varying vec3 vSegPos;',
        'varying vec3 vSegNorm;'
      ].join('\n')
    );

    // Binary search for segment index (~7 steps instead of up to 69)
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <color_fragment>',
      [
        '{',
        '  float axisVal = (uSegAxis == 0) ? vSegPos.x : vSegPos.z;',
        '  int idx = 0;',
        '  int n = uSegCount;',
        '  if (n > 1) {',
        '    int lo = 0;',
        '    int hi = n - 2;',
        '    for (int i = 0; i < 8; i++) {',
        '      if (lo >= hi) break;',
        '      int mid = (lo + hi) / 2;',
        '      if (uSegBounds[mid] < axisVal) lo = mid + 1;',
        '      else hi = mid;',
        '    }',
        '    if (axisVal <= uSegBounds[lo]) idx = lo;',
        '    else idx = lo + 1;',
        '    if (idx >= n) idx = n - 1;',
        '  }',
        '  if (uSegReverse) {',
        '    bool neg = vSegNorm.y < -0.5 || (uSegAxis == 2 && vSegNorm.z < -0.5) || (uSegAxis == 0 && vSegNorm.x < -0.5);',
        '    if (neg) idx = uSegCount - 1 - idx;',
        '  }',
        '  if (idx < 0) idx = 0;',
        '  if (idx >= uSegCount) idx = uSegCount - 1;',
        '  diffuseColor.rgb = uSegColors[idx];',
        '}'
      ].join('\n')
    );
  };

  const boundsKey = boundsArr.slice(0, numSegments - 1).join(',');
  material.customProgramCacheKey = () => `segColor-${axis}-${numSegments}-${reverseNegativeFaces}-${boundsKey}`;

  return material;
}
